// Cash at the counter (docs/plans/phase1-billing.md §5.3).
//
// Amounts are exact to the cent: no rounding to whole shillings (plan §13 decision 11).

import type { Money } from '../money.ts';
import { minMoney, subtractMoney } from '../money.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export type CashErrorCode =
  | 'cash.amount_not_positive'
  | 'cash.tendered_not_positive'
  | 'cash.tendered_less_than_amount'
  | 'cash.nothing_owed';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const CASH_ERRORS: Record<CashErrorCode, string> = {
  'cash.amount_not_positive': 'The amount to pay must be more than zero',
  'cash.tendered_not_positive': 'Enter the cash received',
  'cash.tendered_less_than_amount': 'The cash received is less than the amount to pay',
  'cash.nothing_owed': 'Nothing is owed on this bill',
};

export interface CashTender {
  /** What this payment pays. */
  readonly amount: Money;
  readonly tendered: Money;
  readonly change: Money;
}

/** The change for `tendered` against `amount`. */
export function cashTender(input: {
  readonly amount: Money;
  readonly tendered: Money;
}): Result<CashTender, CashErrorCode> {
  if (input.amount.cents <= 0n) return err('cash.amount_not_positive');
  if (input.tendered.cents <= 0n) return err('cash.tendered_not_positive');
  const change = subtractMoney(input.tendered, input.amount);
  if (change.cents < 0n) return err('cash.tendered_less_than_amount');
  return ok({ amount: input.amount, tendered: input.tendered, change });
}

/**
 * When the cashier types only the cash received: the payment is the lesser of what is
 * owed and what was handed over, so a patient who hands over less pays part of the bill
 * and a patient who hands over more gets change.
 */
export function cashTenderForOwed(input: {
  readonly owed: Money;
  readonly tendered: Money;
}): Result<CashTender, CashErrorCode> {
  if (input.owed.cents <= 0n) return err('cash.nothing_owed');
  if (input.tendered.cents <= 0n) return err('cash.tendered_not_positive');
  return cashTender({ amount: minMoney(input.owed, input.tendered), tendered: input.tendered });
}
